/**
 * Turn Management — skip dead players, track turns, trigger wind shifts + void rise
 */

import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { VOID, VOID_PERSONALITY, MOMENTUM, TERRAIN, CELL, STRUCTURAL, ASCENT, WEAPON_CHARGE } from '../constants';
import { lookupTank } from '../tanks';
import { getGrid, worldToGridX, worldToGridY, gridToWorldX, gridToWorldY, rebuildColumnTopCacheRange } from '../terrain/grid';
import { detectAndCollapseFragments } from '../terrain/structural';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { maybeShiftWind } from './wind';
import { mirrorAirborne, mirrorSettle } from '../physics/transitions';
import { getTankShot } from '../tanks';
import { getDefaultWeapon } from '../weapons';
import { modeUsesLottery, modeUsesShotTiers, modeIsAscent, modeIsRoguelite, modeUsesStocks } from './mode';
import { ROGUELITE } from '../constants';
import { resetPlayerForTurn } from './player-defaults';
import { decrementPortalLifetimes } from './vortex-holes';
import { executeRespawn } from './respawn';
import { rollGlitch } from './glitch';

/** Update dormancy — activate sleeping enemies near the human player. */
function updateDormancy(state: GameState): void {
  if (!modeIsRoguelite(state.modeState.mode)) return;
  const human = state.players[0];
  if (!human || !human.alive) return;

  for (let i = 1; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p.alive || !p.dormant) continue;
    const dy = Math.abs(human.y - p.y);
    if (dy < ROGUELITE.ACTIVATION_RANGE) {
      p.dormant = false;
    }
  }
}

export function advanceTurn(state: GameState, fx: GameEffect[]): void {
  const justFinished = state.currentPlayer; // player who just completed their turn

  state.projectiles = [];
  state.deferredSpawns = [];
  state.firingPhaseKnockedPlayers = [];
  state.turnCount++;

  // Update dormancy before finding next player (roguelite)
  updateDormancy(state);

  // Find next eligible player (alive or dead-with-stocks, and non-dormant)
  const playerCount = state.players.length;
  const hasStocks = modeUsesStocks(state.modeState.mode);

  // Snake turn order: odd cycles (1,3,5...) search forward, even (2,4,6...) search reverse
  const reversed = state.turnNumber % 2 === 0;
  const step = reversed ? -1 : 1;

  let next = ((state.currentPlayer + step) + playerCount) % playerCount;
  for (let attempts = 0; attempts < playerCount; attempts++) {
    const p = state.players[next];
    // In stocks mode: skip only if truly eliminated (dead AND no stocks left)
    const eligible = hasStocks
      ? (p.alive || p.stocks > 0) && !p.dormant
      : p.alive && !p.dormant;
    if (eligible) break;
    next = ((next + step) + playerCount) % playerCount;
  }
  state.currentPlayer = next;

  // New turn cycle when we loop back to the round's starting player
  const isNewTurn = state.currentPlayer === state.startingPlayer;
  if (isNewTurn) {
    state.turnNumber++;

    // Snake: last player of previous cycle starts the new (reversed) cycle
    state.startingPlayer = justFinished;
    const newReversed = state.turnNumber % 2 === 0;
    const newStep = newReversed ? -1 : 1;
    let snakeNext = state.startingPlayer;
    for (let a = 0; a < playerCount; a++) {
      const sp = state.players[snakeNext];
      const elig = hasStocks
        ? (sp.alive || sp.stocks > 0) && !sp.dormant
        : sp.alive && !sp.dormant;
      if (elig) break;
      snakeNext = ((snakeNext + newStep) + playerCount) % playerCount;
    }
    state.currentPlayer = snakeNext;
    state.startingPlayer = snakeNext;

    maybeShiftWind(state);

    // Mood decay happens BEFORE rise — a quiet turn lets the void relax slightly
    state.voidMood = Math.max(0, state.voidMood - VOID_PERSONALITY.MOOD_DECAY_PER_TURN);
    if (state.voidMood > 0) {
      fx.push({ type: 'void_mood_update', mood: state.voidMood });
    }

    maybeRiseVoid(state);

    // Roll glitch event for this turn cycle
    rollGlitch(state, fx);
  }

  // Stocks mode: force-respawn if the selected player is dead but has stocks remaining
  if (hasStocks) {
    const nextP = state.players[state.currentPlayer];
    if (!nextP.alive && nextP.stocks > 0) {
      nextP.respawnTimer = 0;
      const respawnFx = executeRespawn(state, state.currentPlayer);
      fx.push(...respawnFx);
    }
  }

  // Reset the next player's state
  const player = state.players[state.currentPlayer];

  // Carry-over: half of unused momentum as bonus, capped at ABSOLUTE_MAX
  const bonus = Math.floor(player.momentum * MOMENTUM.CARRY_OVER_RATE);
  player.momentum = Math.min(MOMENTUM.MAX + bonus, MOMENTUM.ABSOLUTE_MAX);
  player.momentumTurnStart = player.momentum;

  resetPlayerForTurn(player);

  // Decrement Cupid mark on the marked player's turn
  if (player.cupidMarkTurns > 0) {
    player.cupidMarkTurns--;
    if (player.cupidMarkTurns <= 0) {
      player.cupidMarkedBy = -1;
    }
  }

  // Passive weapon charge fill (shot-tier modes only)
  if (modeUsesShotTiers(state.modeState.mode)) {
    if (state.modeState.mode === 'training') {
      // Training mode: always full so all tiers are available
      player.weaponCharge = lookupTank(player.tankId).chargeSize ?? 100;
    } else {
      const chargeMax = lookupTank(player.tankId).chargeSize ?? 100;
      player.weaponCharge = Math.min(chargeMax, player.weaponCharge + WEAPON_CHARGE.PASSIVE_PER_TURN);
    }
  }

  // Decrement vortex hole lifetimes for the current player (if they're a Vortex tank)
  if (player.tankId === 'vortex') {
    decrementPortalLifetimes(state, state.currentPlayer, fx);
  }

  if (player.settled) mirrorSettle(player); else mirrorAirborne(player);

  // Set default weapon based on mode
  if (modeIsRoguelite(state.modeState.mode)) {
    // Roguelite: human uses compiled shots, AI uses their tank's default
    if (!player.isAI) {
      const ms = state.modeState;
      player.selectedWeapon = ms.rogueliteShot1 ?? getDefaultWeapon();
      // Apply momentum bonus
      player.momentum = Math.min(
        MOMENTUM.ABSOLUTE_MAX,
        player.momentum + ms.rogueliteMomentumBonus,
      );
      player.momentumTurnStart = player.momentum;
    } else {
      player.selectedWeapon = getTankShot(player.tankId, 1, player.terraformerMode).weapon;
    }
  } else if (modeUsesShotTiers(state.modeState.mode)) {
    if (player.armedShotTier !== null) {
      // Armed from previous turn — force armed tier + weapon
      const tier = player.armedShotTier!;
      player.selectedShotTier = tier;
      player.selectedWeapon = getTankShot(player.tankId, tier, player.terraformerMode).weapon;
    } else {
      // Tank battle: use tank's shot tier 1
      player.selectedWeapon = getTankShot(player.tankId, 1, player.terraformerMode).weapon;
    }
  } else if (!modeUsesLottery(state.modeState.mode)) {
    // Standard modes (vs_local, vs_online, training, target_blast): mortar
    player.selectedWeapon = getDefaultWeapon();
  }

}

/** Rise void floor if past the start turn cycle. Sets target — animation happens in updateVoidRise. */
function maybeRiseVoid(state: GameState): void {
  if (state.turnNumber <= VOID.RISE_START_TURN) return;

  state.voidRiseStartY = state.voidY;           // snapshot current position

  // Ascent mode: accelerating rise — void climbs faster each turn cycle (mood does NOT apply)
  if (modeIsAscent(state.modeState.mode)) {
    const riseAmount = ASCENT.VOID_BASE_RISE + ASCENT.VOID_ACCELERATION * state.turnNumber;
    state.voidTargetY -= riseAmount;
  } else {
    // Standard rise scaled by mood: calm = 50px, enraged (mood 100) = up to 90px
    const moodScale = 1 + state.voidMood * VOID_PERSONALITY.RISE_MOOD_SCALE;
    state.voidTargetY -= VOID.RISE_PER_ROUND * moodScale;
  }

  state.voidRisePending = true;
  state.voidRiseTimer = VOID.NOTIFICATION_TIME;
  // voidY is NOT changed here — updateVoidRise animates it
}

/** Animate void Y toward target with smoothstep easing. Called each fixed update (1/60s). */
export function updateVoidRise(state: GameState): void {
  const dt = 1 / 60;
  if (!state.voidRisePending) {
    // Snap safety: ensure voidY reached target
    if (state.voidY > state.voidTargetY) state.voidY = state.voidTargetY;
    return;
  }

  // Elapsed time since rise triggered
  const elapsed = VOID.NOTIFICATION_TIME - state.voidRiseTimer;

  // Movement phase: starts at RISE_ANTICIPATION, lasts RISE_MOVE_DURATION
  if (elapsed >= VOID.RISE_ANTICIPATION && state.voidY > state.voidTargetY) {
    const rawT = Math.min(1, (elapsed - VOID.RISE_ANTICIPATION) / VOID.RISE_MOVE_DURATION);
    const t = rawT * rawT * (3 - 2 * rawT); // smoothstep
    state.voidY = state.voidRiseStartY + (state.voidTargetY - state.voidRiseStartY) * t;
  }

  // Timer
  state.voidRiseTimer -= dt;
  if (state.voidRiseTimer <= 0) {
    state.voidRisePending = false;
    state.voidRiseTimer = 0;
    state.voidY = state.voidTargetY; // snap to final
    state.voidErosionCollapseNeeded = true;
  }
}

// GRID_WIDTH accessed via TERRAIN.GRID_WIDTH (dynamic for wide stages)

/**
 * Erode terrain cells consumed by the rising void. Called every fixed frame,
 * but actual erosion is throttled to every ERODE_THROTTLE_FRAMES frames.
 *
 * Sand is consumed at voidY; wireframe persists WIREFRAME_RESIST px deeper.
 * Sand CA cascade is skipped during rise (sand falling into void is invisible
 * behind the gradient — only structural collapse runs at rise end).
 */
export function erodeTerrainAtVoid(state: GameState): GameEffect[] {
  const grid = getGrid();
  if (!grid) return [];

  const voidY = state.voidY;

  // ── Post-rise structural collapse (runs once when rise ends) ──
  const fx: GameEffect[] = [];
  if (state.voidErosionCollapseNeeded) {
    state.voidErosionCollapseNeeded = false;

    // Scan wireframe in the region the void just swept through
    const minGy = Math.max(0, worldToGridY(state.voidTargetY) - STRUCTURAL.SCAN_EXPAND);
    const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, worldToGridY(state.voidRiseStartY) + STRUCTURAL.SCAN_EXPAND);
    // Skip if void has risen entirely above the grid (bounds inverted)
    const collapse = maxGy >= minGy
      ? detectAndCollapseFragments(grid, { minGx: 0, maxGx: TERRAIN.GRID_WIDTH - 1, minGy, maxGy })
      : null;
    if (collapse) {
      rebuildColumnTopCacheRange(collapse.bounds.minGx, collapse.bounds.maxGx);
      fx.push({ type: 'terrain_update', bounds: collapse.bounds, skipHotEdge: true });
      for (const centroid of collapse.centroids) {
        fx.push({ type: 'particles', method: 'spawnStructuralDebris', args: [centroid.x, centroid.y, collapse.cells] });
      }
      const shakeIntensity = Math.min(
        STRUCTURAL.SHAKE_BASE + collapse.cells * STRUCTURAL.SHAKE_PER_CELL,
        STRUCTURAL.SHAKE_MAX,
      );
      fx.push({ type: 'screen_shake', intensity: shakeIntensity, decay: STRUCTURAL.SHAKE_DECAY });
      spawnSmoothingRegion(state, collapse.bounds);
      for (const centroid of collapse.centroids) {
        for (const p of state.players) {
          if (!p.alive || !p.settled) continue;
          if (Math.abs(p.x - centroid.x) < 60) {
            p.settled = false;
            mirrorAirborne(p);
          }
        }
      }
    }
  }

  // ── Throttled erosion: only run every N frames (void hidden behind gradient) ──
  if (state.frameCount % VOID.ERODE_THROTTLE_FRAMES !== 0) return fx;
  if (voidY >= state.voidLastErodedY) return fx;

  // Compute grid scan region (full width, voidY down to ERODE_SCAN_DEPTH below)
  const topGy = Math.max(0, worldToGridY(voidY));
  const bottomGy = Math.min(TERRAIN.GRID_HEIGHT - 1, worldToGridY(voidY + VOID.ERODE_SCAN_DEPTH));

  // Wireframe resist line in world coords
  const wireframeLine = voidY + VOID.WIREFRAME_RESIST;

  let anyEroded = false;
  let dirtyMinGx: number = TERRAIN.GRID_WIDTH;
  let dirtyMaxGx = 0;
  let dirtyMinGy: number = TERRAIN.GRID_HEIGHT;
  let dirtyMaxGy = 0;

  for (let gy = topGy; gy <= bottomGy; gy++) {
    const cellWorldY = gridToWorldY(gy);
    const rowOffset = gy * TERRAIN.GRID_WIDTH;
    for (let gx = 0; gx < TERRAIN.GRID_WIDTH; gx++) {
      const cell = grid[rowOffset + gx];
      if (cell === CELL.EMPTY) continue;

      let shouldClear = false;
      if (cell === CELL.SAND) {
        // Sand consumed at/below void surface
        if (cellWorldY >= voidY) shouldClear = true;
      } else if (cell === CELL.WIREFRAME) {
        // Wireframe persists slightly longer — consumed deeper
        if (cellWorldY >= wireframeLine) shouldClear = true;
      }

      if (shouldClear) {
        grid[rowOffset + gx] = CELL.EMPTY;
        anyEroded = true;
        if (gx < dirtyMinGx) dirtyMinGx = gx;
        if (gx > dirtyMaxGx) dirtyMaxGx = gx;
        if (gy < dirtyMinGy) dirtyMinGy = gy;
        if (gy > dirtyMaxGy) dirtyMaxGy = gy;
      }
    }
  }

  if (!anyEroded) {
    state.voidLastErodedY = voidY;
    return fx;
  }

  state.voidLastErodedY = voidY;

  // Rebuild caches for dirty columns
  rebuildColumnTopCacheRange(dirtyMinGx, dirtyMaxGx);

  const dirtyBounds = { minGx: dirtyMinGx, maxGx: dirtyMaxGx, minGy: dirtyMinGy, maxGy: dirtyMaxGy };
  fx.push({ type: 'terrain_update', bounds: dirtyBounds, skipHotEdge: true });

  // Sand CA cascade skipped during rise — sand falling into void is invisible
  // behind the gradient. Structural collapse at rise end handles loose fragments.

  // Unsettle tanks whose footing was eroded
  for (const p of state.players) {
    if (!p.alive || !p.settled) continue;
    const pgx = worldToGridX(p.x);
    if (pgx >= dirtyMinGx && pgx <= dirtyMaxGx) {
      p.settled = false;
      mirrorAirborne(p);
    }
  }

  // Periodic crumbling particles along erosion line
  if (state.voidRisePending && state.frameCount % VOID.ERODE_PARTICLE_INTERVAL === 0) {
    const count = VOID.ERODE_PARTICLE_COUNT;
    // Distribute particle bursts across the erosion line width
    for (let i = 0; i < count; i++) {
      const wx = gridToWorldX(dirtyMinGx + Math.floor(Math.random() * (dirtyMaxGx - dirtyMinGx + 1)));
      const wy = voidY - 5 + Math.random() * 10; // near void surface
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [wx, wy, 15, 0.3] });
    }
  }

  return fx;
}
